// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — what a room's activity draws on the browser globe
 * (`docs/MEETINGS_COLLAB_PLAN.md`): the heat map of where people look,
 * the pins they dropped, the answers to a question, this person's own
 * answer, and the right place once revealed; and the taps that answer.
 *
 * MapLibre layers and markers on the primary viewport's map. A basemap
 * change replaces the style and with it every source, so what is shown
 * is kept here and put back on each `styledata`. The map itself can be
 * replaced (a layout change), so it is asked for on every call.
 */

import * as maplibregl from 'maplibre-gl'
import type { GeoJSONSource, Map as MaplibreMap, MapMouseEvent } from 'maplibre-gl'
import { styleReady } from './maplibreInternals'
import type { LookCell, RoomPin } from './roomActivity'

export interface PinMarker extends RoomPin {
  /** This person's own pin. */
  own?: boolean
}

export interface ActivityGlobe {
  /** Where people are looking; empty clears it. */
  setHeat(cells: readonly LookCell[]): void
  /** The pins; `onShow` adds a "Show everyone" action to each (the lead). */
  setPins(pins: readonly PinMarker[], onShow?: (pin: RoomPin) => void): void
  /** Answers as dots, and the right place if there is one; empty clears them. */
  setAnswers(points: readonly [number, number][], answer: [number, number] | null): void
  /** This person's own answer or pin-to-be; null clears it. */
  setMine(point: [number, number] | null): void
  /** Called with [lat, lon] when the globe is tapped. Returns an unsubscribe. */
  onTap(listener: (point: [number, number]) => void): () => void
  /** Where the camera looks, [lat, lon]. */
  center(): [number, number] | null
  flyTo(point: [number, number]): void
  /** Take everything off the globe. */
  clear(): void
}

const HEAT = 'ayni-activity-heat'
const DOTS = 'ayni-activity-dots'
const ANSWER = 'ayni-activity-answer'

type Feature = GeoJSON.Feature<GeoJSON.Point, Record<string, unknown>>
const collection = (features: Feature[]): GeoJSON.FeatureCollection<GeoJSON.Point> => ({ type: 'FeatureCollection', features })
const point = (lat: number, lon: number, properties: Record<string, unknown> = {}): Feature => ({
  type: 'Feature',
  geometry: { type: 'Point', coordinates: [lon, lat] },
  properties,
})

/** The overlay on whatever map `getMap` returns now. */
export function mapActivityGlobe(
  getMap: () => MaplibreMap | null,
  labels: { showEveryone: string; anonymous: string; answer: string },
): ActivityGlobe {
  let heat: Feature[] = []
  let dots: Feature[] = []
  let answer: Feature[] = []
  let attached: MaplibreMap | null = null
  let markers: maplibregl.Marker[] = []
  let mine: maplibregl.Marker | null = null
  const tapListeners = new Set<(p: [number, number]) => void>()

  const onClick = (ev: MapMouseEvent): void => {
    const { lat, lng } = ev.lngLat
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return
    const lon = ((((lng + 180) % 360) + 360) % 360) - 180
    for (const listener of tapListeners) listener([lat, lon])
  }

  /** Make sure the sources and layers are on this map's current style. */
  function ensureLayers(map: MaplibreMap): void {
    // AYNI: the style itself, not every tile (`isStyleLoaded` also waits for every source; maplibreInternals.ts).
    if (!styleReady(map)) return
    if (!map.getSource(HEAT)) {
      map.addSource(HEAT, { type: 'geojson', data: collection(heat) })
      map.addLayer({
        id: HEAT,
        type: 'heatmap',
        source: HEAT,
        paint: {
          'heatmap-weight': ['get', 'w'],
          'heatmap-intensity': 1,
          'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 0, 18, 4, 40, 8, 80],
          'heatmap-opacity': 0.75,
          'heatmap-color': [
            'interpolate', ['linear'], ['heatmap-density'],
            0, 'rgba(0,0,0,0)', 0.2, 'rgba(34,197,94,0.55)', 0.5, 'rgba(234,179,8,0.75)', 0.8, 'rgba(249,115,22,0.85)', 1, 'rgba(239,68,68,0.95)',
          ],
        },
      })
    }
    if (!map.getSource(DOTS)) {
      map.addSource(DOTS, { type: 'geojson', data: collection(dots) })
      map.addLayer({
        id: DOTS,
        type: 'circle',
        source: DOTS,
        paint: {
          'circle-radius': 5,
          'circle-color': '#38bdf8',
          'circle-opacity': 0.85,
          'circle-stroke-color': '#0b1220',
          'circle-stroke-width': 1,
        },
      })
    }
    if (!map.getSource(ANSWER)) {
      map.addSource(ANSWER, { type: 'geojson', data: collection(answer) })
      map.addLayer({
        id: ANSWER,
        type: 'circle',
        source: ANSWER,
        paint: {
          'circle-radius': 9,
          'circle-color': '#22c55e',
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 3,
        },
      })
    }
  }

  const onStyle = (): void => {
    if (attached) ensureLayers(attached)
  }

  /** The map now, with the overlay attached to it. */
  function map(): MaplibreMap | null {
    const current = getMap()
    if (current !== attached) {
      attached?.off('click', onClick)
      attached?.off('styledata', onStyle)
      attached = current
      attached?.on('click', onClick)
      attached?.on('styledata', onStyle)
    }
    if (attached) ensureLayers(attached)
    return attached
  }

  function setData(id: string, features: Feature[]): void {
    const m = map()
    ;(m?.getSource(id) as GeoJSONSource | undefined)?.setData(collection(features))
  }

  /**
   * A pin's marker: the element MapLibre positions (its transform is
   * MapLibre's), with the teardrop shape inside it. Turning the outer
   * element itself would compose with that transform and throw the pin
   * hundreds of pixels off its place.
   */
  function pinElement(className: string, label: string): HTMLElement {
    const el = document.createElement('div')
    el.className = className
    el.setAttribute('aria-label', label)
    el.append(Object.assign(document.createElement('span'), { className: 'activity-pin-shape' }))
    return el
  }

  return {
    setHeat(cells) {
      const max = Math.max(1, ...cells.map((c) => c[2]))
      heat = cells.map(([lat, lon, n]) => point(lat, lon, { w: n / max }))
      setData(HEAT, heat)
    },
    setPins(pins, onShow) {
      for (const marker of markers) marker.remove()
      markers = []
      const m = map()
      if (!m) return
      for (const pin of pins) {
        const content = document.createElement('div')
        content.className = 'activity-pin-popup'
        const who = document.createElement('strong')
        who.textContent = pin.name ?? labels.anonymous
        const note = document.createElement('p')
        note.textContent = pin.note
        content.append(who, note)
        if (onShow) {
          const show = document.createElement('button')
          show.type = 'button'
          show.textContent = labels.showEveryone
          show.addEventListener('click', () => onShow(pin))
          content.append(show)
        }
        const marker = new maplibregl.Marker({ element: pinElement(`activity-pin${pin.own ? ' own' : ''}`, pin.note || labels.anonymous), anchor: 'bottom' })
          .setLngLat([pin.lon, pin.lat])
          .setPopup(new maplibregl.Popup({ offset: 18, className: 'activity-popup', closeButton: true }).setDOMContent(content))
          .addTo(m)
        markers.push(marker)
      }
    },
    setAnswers(points, right) {
      dots = points.map(([lat, lon]) => point(lat, lon))
      answer = right ? [point(right[0], right[1], { label: labels.answer })] : []
      setData(DOTS, dots)
      setData(ANSWER, answer)
    },
    setMine(p) {
      mine?.remove()
      mine = null
      const m = map()
      if (!p || !m) return
      mine = new maplibregl.Marker({ element: pinElement('activity-pin mine', labels.answer), anchor: 'bottom' }).setLngLat([p[1], p[0]]).addTo(m)
    },
    onTap(listener) {
      map()
      tapListeners.add(listener)
      return () => tapListeners.delete(listener)
    },
    center() {
      const c = map()?.getCenter()
      return c ? [c.lat, ((((c.lng + 180) % 360) + 360) % 360) - 180] : null
    },
    flyTo([lat, lon]) {
      const m = map()
      m?.flyTo({ center: [lon, lat], zoom: Math.max(m.getZoom(), 3), duration: 1800 })
    },
    clear() {
      heat = []
      dots = []
      answer = []
      setData(HEAT, heat)
      setData(DOTS, dots)
      setData(ANSWER, answer)
      for (const marker of markers) marker.remove()
      markers = []
      mine?.remove()
      mine = null
    },
  }
}
